// components/UserPermissionsModal.tsx
import React, { useCallback, useState } from 'react';
import { Shield, X } from '@/components/ui/lucide-memo';
import SimpleLoader from './ui/SimpleLoader';
import Switch from './ui/Switch';
import { useKernelStore, useKernelStoreApi } from '@/kernel/kernelStores';
import { BASE_BOOLEAN_PERMISSIONS, UserPermissions } from '#types/types';

interface UserPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onSubmit: (userId: string, permissions: UserPermissions) => Promise<void>;
}

const permissionCopy: Record<keyof typeof BASE_BOOLEAN_PERMISSIONS, { label: string; description: string }> = {
  canSeeDevOnlyDisplays: {
    label: 'Developer displays',
    description: 'Show internal diagnostic information in the interface.',
  },
  canAccessAssetManagement: {
    label: 'Asset management',
    description: 'Allow access to asset-management tools and workflows.',
  },
  canAccessFactory: {
    label: 'Factory and List',
    description: 'Show the special Factory and List controls in Studio.',
  },
  canAccessVector: {
    label: 'Studio Vector',
    description: 'Show the Studio Vector tool, the vector editor for logos, icons and plans.',
  },
  canAccessSubjectTimelineView: {
    label: 'Subject timeline',
    description: 'Allow access to the subject-level activity timeline.',
  },
  canAccessFloorPlanTools: {
    label: 'Floor plan tools',
    description: 'Allow the floor plan mapper, the 3D view and the floor foundation editor.',
  },
};

const selectClass =
  'h-8 min-w-44 rounded-md border border-[#2A2A2A] bg-[#141414] px-2 text-xs text-white/75 outline-none transition-colors hover:border-white/15 focus:border-white/20 disabled:opacity-40';

const stopPropagation = (event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation();

type PermissionRowProps = {
  permissionKey: keyof typeof BASE_BOOLEAN_PERMISSIONS;
  checked: boolean;
  disabled: boolean;
  onToggle: (key: keyof typeof BASE_BOOLEAN_PERMISSIONS) => void;
};

const PermissionRow: React.FC<PermissionRowProps> = React.memo(({ permissionKey, checked, disabled, onToggle }) => {
  const copy = permissionCopy[permissionKey];
  const handleCheckedChange = useCallback(() => onToggle(permissionKey), [onToggle, permissionKey]);

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-white/75">{copy.label}</p>
        <p className="mt-0.5 text-xs text-white/35">{copy.description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={handleCheckedChange}
        labelRight={null}
        ariaLabel={copy.label}
        disabled={disabled}
      />
    </div>
  );
});
PermissionRow.displayName = 'PermissionRow';

const UserPermissionsModal: React.FC<UserPermissionsModalProps> = ({
  isOpen,
  onClose,
  userId,
  onSubmit,
}) => {
  const userEmail = useKernelStore((state) => state.inventory.User.registry[userId]?.email ?? '');
  const kernelStore = useKernelStoreApi();
  // Le registre est lu une fois au montage, au getter plutôt qu'en abonnement : une mise à
  // jour socket du User pendant l'édition écraserait sinon les bascules en cours. L'appelant
  // monte le modal à l'ouverture et le remonte par key quand l'utilisateur change.
  const [permissions, setPermissions] = useState<UserPermissions>(
    () => kernelStore.getState().inventory.User.registry[userId]?.permissions || {},
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = useCallback((key: keyof typeof BASE_BOOLEAN_PERMISSIONS) => {
    setPermissions((previous) => ({ ...previous, [key]: !previous[key] }));
  }, []);

  const handleAccessScopeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const accessScope = value === 'ALL_MODELS' || value === 'CHEAP_MODELS_ONLY' ? value : undefined;
    setPermissions((previous) => ({ ...previous, accessScope }));
  }, []);

  const handleCreditVisibilityChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const creditVisibility = value === 'ORG' || value === 'PERSONAL' ? value : undefined;
    setPermissions((previous) => ({ ...previous, creditVisibility }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    try {
      await onSubmit(userId, permissions);
      onClose();
    } catch (error) {
      console.error('[Permissions] Save failed', error);
    } finally {
      setIsSaving(false);
    }
  }, [onSubmit, userId, permissions, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[102] flex items-center justify-center bg-[#141414]/90 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121] text-white shadow-2xl"
        onClick={stopPropagation}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-md p-1.5 text-white/35 transition-colors hover:bg-white/[0.06] hover:text-white/80"
          disabled={isSaving}
          aria-label="Close modal"
        >
          <X size={17} />
        </button>

        <header className="border-b border-white/[0.055] px-5 py-4 pr-12">
          <div className="flex items-center gap-2">
            <Shield size={16} className="text-white/35" />
            <h2 className="text-lg font-semibold text-white/90">User permissions</h2>
          </div>
          <p className="mt-1 truncate text-sm text-white/45" title={userEmail}>{userEmail}</p>
        </header>

        <div className="divide-y divide-white/[0.055] px-5">
          {Object.keys(BASE_BOOLEAN_PERMISSIONS).map((key) => {
            const permissionKey = key as keyof typeof BASE_BOOLEAN_PERMISSIONS;
            return (
              <PermissionRow
                key={permissionKey}
                permissionKey={permissionKey}
                checked={Boolean(permissions[permissionKey])}
                disabled={isSaving}
                onToggle={handleToggle}
              />
            );
          })}

          <div className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div>
              <p className="text-sm font-medium text-white/75">Model access</p>
              <p className="mt-0.5 text-xs text-white/35">Choose which generation models this user can run.</p>
            </div>
            <select
              value={permissions.accessScope ?? ''}
              onChange={handleAccessScopeChange}
              className={selectClass}
              disabled={isSaving}
              aria-label="Model access"
            >
              <option value="">Organisation plan default</option>
              <option value="CHEAP_MODELS_ONLY">Cost-efficient models only</option>
              <option value="ALL_MODELS">All models</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="max-w-xs">
              <p className="text-sm font-medium text-white/75">Credit visibility</p>
              <p className="mt-0.5 text-xs text-white/35">Choose which remaining-credit balance appears in the app.</p>
            </div>
            <select
              value={permissions.creditVisibility ?? 'HIDDEN'}
              onChange={handleCreditVisibilityChange}
              className={selectClass}
              disabled={isSaving}
              aria-label="Credit visibility"
            >
              <option value="HIDDEN">Hidden</option>
              <option value="ORG">Organisation credits</option>
              <option value="PERSONAL">Personal credits</option>
            </select>
          </div>
        </div>

        <footer className="flex justify-end gap-2 border-t border-white/[0.055] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 items-center rounded-md px-3 text-sm text-white/60 transition-colors hover:bg-white/[0.06] hover:text-white/85 disabled:opacity-40"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="inline-flex h-8 min-w-24 items-center justify-center rounded-md bg-white px-3 text-sm font-medium text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isSaving ? <SimpleLoader size={14} message="" /> : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default UserPermissionsModal;
